import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { ButtonComponent } from "../../../components/elements";
import { Modal, Row, Col, Form, Table, Badge } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import Swal from 'sweetalert2';
import { useNavigate } from "react-router-dom";
import Select from 'react-select';

const usePendingPaymentsData = () => {
    const [pendingData, setPendingData] = useState([]);
    const [totalDue, setTotalDue] = useState(0);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);

    const token = localStorage.getItem("authToken");
    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const fetchPendingPayments = useCallback(async (customerId = null) => {
        setLoading(true);
        try {
            let url = `${process.env.REACT_APP_API_URL}/pending-payments`;
            if (customerId) {
                url += `?customer_id=${customerId}`;
            }
            const res = await axios.get(url, { headers });
            if (res.data.status) {
                setPendingData(res.data.data || []);
                setTotalDue(res.data.total_due || 0);
                if (res.data.customers) {
                    setCustomers(res.data.customers);
                }
            }
        } catch (err) {
            console.error(err);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: err.response?.data?.message || 'Failed to fetch pending payments',
            });
        } finally {
            setLoading(false);
        }
    }, [headers]);

    useEffect(() => {
        fetchPendingPayments();
    }, [fetchPendingPayments]);

    return { pendingData, totalDue, customers, loading, fetchPendingPayments, headers };
};

export default function PendingPaymentIndex() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();

    const permissions = useMemo(() => JSON.parse(localStorage.getItem("permissions")) || [], []);
    const hasPermission = useCallback((perm) => permissions.includes(perm), [permissions]);

    const { pendingData, totalDue, customers, loading, fetchPendingPayments, headers } = usePendingPaymentsData();

    // State for filters
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    // State for modals
    const [detailModal, setDetailModal] = useState(false);
    const [paymentModal, setPaymentModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    // Payment form state
    const [paymentForm, setPaymentForm] = useState({
        amount: '',
        payment_method_id: '',
        payment_date: new Date().toISOString().split('T')[0],
        note: ''
    });

    const [paymentMethods, setPaymentMethods] = useState([]);

    // Customer options for react-select
    const customerOptions = useMemo(() => {
        return [
            {
                value: '',
                label: t('All Customers')
            },
            ...customers.map(customer => ({
                value: customer.id,
                label: customer.phone
                    ? `${customer.name} - (${customer.phone})`
                    : customer.name
            }))
        ];
    }, [customers, t]);

    // Fetch payment methods when payment modal opens
    useEffect(() => {
        if (paymentModal) {
            fetchPaymentMethods();
        }
    }, [paymentModal]);

    const fetchPaymentMethods = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/payment-methods`, { headers });
            if (res.data) {
                setPaymentMethods(res.data);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? '—' : `${date.getDate()}-${date.toLocaleString('en-US', { month: 'short' })}-${date.getFullYear()}`;
    };

    const formatCurrency = (val) => {
        const num = Number(val || 0);
        return num % 1 === 0
            ? num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
            : num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const handleCustomerFilter = (selected) => {
        setSelectedCustomer(selected);
        fetchPendingPayments(selected?.value || null);
    };

    const handleClearFilter = () => {
        setSelectedCustomer(null);
        fetchPendingPayments(null);
    };

    const handleViewDetails = (item) => {
        setSelectedItem(item);
        setDetailModal(true);
    };

    const handlePayment = (item) => {
        setSelectedItem(item);
        setPaymentForm({
            amount: item.due_amount || 0,
            payment_method_id: '',
            payment_date: new Date().toISOString().split('T')[0],
            note: ''
        });
        setPaymentModal(true);
    };

    const handlePaymentInputChange = (e) => {
        const { name, value } = e.target;
        setPaymentForm(prev => ({ ...prev, [name]: value }));
    };

    const handlePaymentSubmit = async (e) => {
        e.preventDefault();

        if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: t('Please enter a valid amount'),
            });
            return;
        }

        if (parseFloat(paymentForm.amount) > parseFloat(selectedItem.due_amount)) {
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: t('Payment amount cannot exceed due amount'),
            });
            return;
        }

        try {
            const payload = {
                type: selectedItem.type,
                id: selectedItem.id,
                amount: parseFloat(paymentForm.amount),
                payment_method_id: paymentForm.payment_method_id || null,
                payment_date: paymentForm.payment_date,
                note: paymentForm.note
            };

            await axios.post(`${process.env.REACT_APP_API_URL}/pending-payments`, payload, { headers });

            setPaymentModal(false);
            setSelectedItem(null);
            // Refresh with current filter
            fetchPendingPayments(selectedCustomer?.value || null);

            Swal.fire({
                icon: 'success',
                title: t('success'),
                text: t('Payment recorded successfully'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: error.response?.data?.message || t('Failed to record payment'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        }
    };

    const handleNavigateToDetail = (item) => {
        if (item.type === 'sale') {
            navigate(`/sale-edit/${item.id}`);
        } else if (item.type === 'appointment') {
            navigate(`/appointments/${item.id}`, {
                state: {
                    activeTab: "invoice"
                }
            });
        }
        setDetailModal(false);
    };

    // Loading state
    if (loading) {
        return (
            <PageLayout>
                <div className="p-5 text-center">
                    <div className="spinner-border text-primary" role="status"></div>
                    <p className="mt-2">{t("loading")}...</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row className="mb-4">
                <Col xl={12}>
                    <div className="card shadow-sm border-0">
                        <div className="card-body p-3 bg-light rounded d-flex align-items-center justify-content-between">
                            <div>
                                <h3 className="mb-0 text-dark fw-bold text-capitalize" style={{ fontSize: '1.5rem' }}>
                                    {t('pending payments')}
                                </h3>
                            </div>
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb mb-0 small font-weight-medium">
                                    <li className="breadcrumb-item text-muted">{t('dashboard')}</li>
                                    <li className="breadcrumb-item active text-primary" aria-current="page">{t('Pending invoices')}</li>
                                </ol>
                            </nav>
                        </div>
                    </div>
                </Col>
            </Row>

            <Row>
                <Col xl={12}>
                    <div className="card shadow-sm border-0">
                        {/* <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center border-bottom">
                            <h5 className="mb-0 text-secondary font-weight-bold">{t('Pending Invoices list')}</h5>
                            <div className="d-flex gap-2">
                                <ButtonComponent
                                    className="btn btn-primary btn-md px-4 shadow-sm"
                                    onClick={() => fetchPendingPayments(selectedCustomer?.value || null)}
                                >
                                    <i className="material-icons align-middle me-1">refresh</i>
                                    <span className="align-middle">{t('refresh')}</span>
                                </ButtonComponent>
                            </div>
                        </div> */}

                        {/* Filter Section */}
                        <div className="card-body bg-light border-bottom p-3">
                            <Row className="g-3 align-items-end">
                                <Col md={6} lg={4}>
                                    <Form.Label className="small fw-bold text-muted mb-1">
                                        {t('Filter by Customer')}
                                    </Form.Label>
                                    <Select
                                        options={customerOptions}
                                        value={selectedCustomer}
                                        onChange={handleCustomerFilter}
                                        placeholder={t('Search customer...')}
                                        isSearchable
                                        isClearable
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '38px',
                                                borderColor: '#ced4da',
                                                '&:hover': {
                                                    borderColor: '#86b7fe'
                                                }
                                            })
                                        }}
                                    />
                                </Col>
                                <Col md={6} lg={2}>
                                    <ButtonComponent
                                        className="btn btn-sm btn-secondary px-3 w-100"
                                        onClick={handleClearFilter}
                                    >
                                        <i className="material-icons size-sm me-1 align-middle">clear</i>
                                        {t('Clear Filter')}
                                    </ButtonComponent>
                                </Col>
                                <Col md={12} lg={6}>
                                    <div className="d-flex justify-content-end gap-3">
                                        <div className="text-muted small">
                                            {t('Total Items')}: <span className="fw-bold">{pendingData.length}</span>
                                        </div>
                                        <div className="text-muted small">
                                            {t('Total Due')}: <span className="fw-bold text-danger">{formatCurrency(totalDue)}</span>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        <div className="table-responsive">
                            <Table hover className="align-middle mb-0 custom-data-table">
                                <thead className="table-light text-uppercase font-weight-bold small text-muted">
                                    <tr>
                                        <th style={{ width: '60px' }}>#</th>
                                        <th>{t('type')}</th>
                                        <th>{t('number')}</th>
                                        <th>{t('date')}</th>
                                        <th>{t('customer')}</th>
                                        <th className="text-end">{t('total amount')}</th>
                                        <th className="text-end">{t('paid amount')}</th>
                                        <th className="text-end">{t('due amount')}</th>
                                        <th style={{ width: '150px' }} className="text-center">{t('actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pendingData.length === 0 ? (
                                        <tr>
                                            <td colSpan="9" className="text-center py-5 text-muted">
                                                <i className="material-icons display-4 text-opacity-25 text-dark mb-2">check_circle</i>
                                                <p className="mb-0 font-weight-bold">{t('no pending payments found')}</p>
                                                <p className="small text-muted">{t('All payments are up to date')}</p>
                                            </td>
                                        </tr>
                                    ) : (
                                        pendingData.map((item, index) => (
                                            <tr key={`${item.type}-${item.id}`} className="transition-all">
                                                <td className="text-muted small">{index + 1}</td>
                                                <td>
                                                    <Badge bg={item.type === 'sale' ? 'primary' : 'success'}>
                                                        {item.type === 'sale' ? t('Sale') : t('Appointment')}
                                                    </Badge>
                                                </td>
                                                <td className="fw-bold text-dark">{item.number || '—'}</td>
                                                <td>{formatDate(item.date)}</td>
                                                <td>{item.customer || '—'}</td>
                                                <td className="text-end fw-bold">{formatCurrency(item.total_amount)}</td>
                                                <td className="text-end text-success fw-bold">{formatCurrency(item.paid_amount)}</td>
                                                <td className="text-end text-danger fw-bold">{formatCurrency(item.due_amount)}</td>
                                                <td className="text-center">
                                                    <div className="d-flex justify-content-center gap-1.5">
                                                        <button
                                                            className="btn btn-icon btn-sm btn-light text-info rounded-circle shadow-none"
                                                            onClick={() => handleViewDetails(item)}
                                                            title={t('view details')}
                                                        >
                                                            <i className="material-icons" style={{ fontSize: '18px' }}>visibility</i>
                                                        </button>
                                                        {parseFloat(item.due_amount) > 0 && (
                                                            <button
                                                                className="btn btn-icon btn-sm btn-light text-success rounded-circle shadow-none"
                                                                onClick={() => handlePayment(item)}
                                                                title={t('record payment')}
                                                            >
                                                                <i className="material-icons" style={{ fontSize: '18px' }}>payments</i>
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        {pendingData.length > 0 && (
                            <div className="card-footer bg-white border-top py-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="text-muted small">
                                        {t('Showing')} <span className="fw-bold">{pendingData.length}</span> {t('items')}
                                    </div>
                                    <div className="text-muted small">
                                        {t('Total Due')}: <span className="fw-bold text-danger">{formatCurrency(totalDue)}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            {/* Detail Modal */}
            <Modal show={detailModal} onHide={() => setDetailModal(false)} size="lg" centered>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="fw-bold">
                        <i className="material-icons align-middle me-2">info</i>
                        {t('Payment Details')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedItem && (
                        <div>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <div className="p-3 bg-light rounded">
                                        <small className="text-muted d-block">{t('Type')}</small>
                                        <strong className="text-capitalize">
                                            <Badge bg={selectedItem.type === 'sale' ? 'primary' : 'success'}>
                                                {selectedItem.type}
                                            </Badge>
                                        </strong>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="p-3 bg-light rounded">
                                        <small className="text-muted d-block">{t('Number')}</small>
                                        <strong>{selectedItem.number || '—'}</strong>
                                    </div>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <div className="p-3 bg-light rounded">
                                        <small className="text-muted d-block">{t('Date')}</small>
                                        <strong>{formatDate(selectedItem.date)}</strong>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="p-3 bg-light rounded">
                                        <small className="text-muted d-block">{t('Customer')}</small>
                                        <strong>{selectedItem.customer || '—'}</strong>
                                    </div>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={4}>
                                    <div className="p-3 bg-light rounded">
                                        <small className="text-muted d-block">{t('Total Amount')}</small>
                                        <strong className="fs-5">{formatCurrency(selectedItem.total_amount)}</strong>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="p-3 bg-light rounded">
                                        <small className="text-muted d-block">{t('Paid Amount')}</small>
                                        <strong className="fs-5 text-success">{formatCurrency(selectedItem.paid_amount)}</strong>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="p-3 bg-light rounded">
                                        <small className="text-muted d-block">{t('Due Amount')}</small>
                                        <strong className="fs-5 text-danger">{formatCurrency(selectedItem.due_amount)}</strong>
                                    </div>
                                </Col>
                            </Row>

                            <div className="mt-4">
                                <h6 className="fw-bold mb-3">{t('Items Summary')}</h6>
                                <div className="table-responsive">
                                    <Table size="sm" bordered className="mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>#</th>
                                                <th>{t('Item')}</th>
                                                <th className="text-end">{t('Amount')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedItem.summary && selectedItem.summary.length > 0 ? (
                                                selectedItem.summary
                                                    .filter(item => (item.line_total || item.price || 0) > 0)
                                                    .map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td>{idx + 1}</td>
                                                            <td>{item.product_name || item.name || '—'}</td>
                                                            <td className="text-end">
                                                                {formatCurrency(item.line_total || item.price || 0)}
                                                            </td>
                                                        </tr>
                                                    ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="3" className="text-center text-muted">
                                                        {t('No items found')}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                        <tfoot className="table-light">
                                            <tr>
                                                <td colSpan="2" className="fw-bold text-end">{t('Total')}</td>
                                                <td className="fw-bold text-end">{formatCurrency(selectedItem.total_amount)}</td>
                                            </tr>
                                        </tfoot>
                                    </Table>
                                </div>
                            </div>

                            <div className="mt-4 d-flex justify-content-end gap-2">
                                <ButtonComponent
                                    className="btn btn-sm btn-info px-3"
                                    onClick={() => handleNavigateToDetail(selectedItem)}
                                >
                                    <i className="material-icons align-middle me-1" style={{ fontSize: '18px' }}>open_in_new</i>
                                    {t('More Details')}
                                </ButtonComponent>
                                {parseFloat(selectedItem.due_amount) > 0 && (
                                    <ButtonComponent
                                        className="btn btn-sm btn-success px-3"
                                        onClick={() => {
                                            setDetailModal(false);
                                            handlePayment(selectedItem);
                                        }}
                                    >
                                        <i className="material-icons align-middle me-1" style={{ fontSize: '18px' }}>payments</i>
                                        {t('Record Payment')}
                                    </ButtonComponent>
                                )}
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <ButtonComponent className="btn btn-sm btn-secondary border" onClick={() => setDetailModal(false)}>
                        {t('Close')}
                    </ButtonComponent>
                </Modal.Footer>
            </Modal>

            {/* Payment Modal */}
            <Modal show={paymentModal} onHide={() => setPaymentModal(false)} size="md" centered>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="fw-bold">
                        <i className="material-icons align-middle me-2">payments</i>
                        {t('Record Payment')}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handlePaymentSubmit}>
                    <Modal.Body>
                        {selectedItem && (
                            <div className="mb-3 p-3 bg-light rounded">
                                <Row>
                                    <Col xs={6}>
                                        <small className="text-muted d-block">{t('Type')}</small>
                                        <Badge bg={selectedItem.type === 'sale' ? 'primary' : 'success'}>
                                            {selectedItem.type}
                                        </Badge>
                                    </Col>
                                    <Col xs={6}>
                                        <small className="text-muted d-block">{t('Number')}</small>
                                        <strong>{selectedItem.number || '—'}</strong>
                                    </Col>
                                    <Col xs={12} className="mt-2">
                                        <small className="text-muted d-block">{t('Customer')}</small>
                                        <strong>{selectedItem.customer || '—'}</strong>
                                    </Col>
                                    <Col xs={6} className="mt-2">
                                        <small className="text-muted d-block">{t('Total Amount')}</small>
                                        <strong>{formatCurrency(selectedItem.total_amount)}</strong>
                                    </Col>
                                    <Col xs={6} className="mt-2">
                                        <small className="text-muted d-block">{t('Due Amount')}</small>
                                        <strong className="text-danger">{formatCurrency(selectedItem.due_amount)}</strong>
                                    </Col>
                                </Row>
                            </div>
                        )}

                        <Row className="g-3">
                            <Col md={12}>
                                <Form.Label className="small fw-bold text-secondary mb-1">
                                    {t('Amount')} <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    name="amount"
                                    className="shadow-none"
                                    value={paymentForm.amount}
                                    onChange={handlePaymentInputChange}
                                    required
                                    min="0.01"
                                    max={selectedItem?.due_amount || 0}
                                />
                                <Form.Text className="text-muted">
                                    {t('Maximum')}: {formatCurrency(selectedItem?.due_amount || 0)}
                                </Form.Text>
                            </Col>
                            <Col md={12}>
                                <Form.Label className="small fw-bold text-secondary mb-1">
                                    {t('Payment Method')}
                                </Form.Label>
                                <Form.Select
                                    name="payment_method_id"
                                    className="shadow-none"
                                    value={paymentForm.payment_method_id}
                                    onChange={handlePaymentInputChange}
                                >
                                    <option value="">{t('Select Payment Method')}</option>
                                    {paymentMethods.map(method => (
                                        <option key={method.id} value={method.id}>
                                            {method.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col md={12}>
                                <Form.Label className="small fw-bold text-secondary mb-1">
                                    {t('Payment Date')}
                                </Form.Label>
                                <Form.Control
                                    type="date"
                                    name="payment_date"
                                    className="shadow-none"
                                    value={paymentForm.payment_date}
                                    onChange={handlePaymentInputChange}
                                />
                            </Col>
                            <Col md={12}>
                                <Form.Label className="small fw-bold text-secondary mb-1">
                                    {t('Note')}
                                </Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    name="note"
                                    className="shadow-none"
                                    value={paymentForm.note}
                                    onChange={handlePaymentInputChange}
                                    placeholder={t('Optional notes')}
                                />
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <ButtonComponent type="button" className="btn btn-sm btn-light border" onClick={() => setPaymentModal(false)}>
                            {t('Cancel')}
                        </ButtonComponent>
                        <ButtonComponent type="submit" className="btn btn-sm btn-success px-4 shadow-sm">
                            <i className="material-icons align-middle me-1" style={{ fontSize: '18px' }}>check</i>
                            {t('Confirm Payment')}
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>
        </PageLayout>
    );
}